import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService, NotifyService } from '@app/services';
import { TranslatableKey } from '@app/types/translatable.type';
import { RoleNameDisplayOptions } from '@app/types/translatables/role-display.options';
import { UpdateView } from '@common/views';
import { Role } from '@models/account/role.model';
import { ApprovalWorkflow } from '@models/approvals/approval-workflow.model';
import { ChangeRequestTemplate } from '@models/change-requests/change-request-template.model';

@Component({
    templateUrl: './change-request-template-update.template.html',
    styleUrls: ['./change-request-template-update.style.scss'],
})
export class ChangeRequestTemplateUpdateView extends UpdateView implements OnInit {
    changeRequestTemplate: ChangeRequestTemplate;
    approvalWorkflow: ApprovalWorkflow;
    isLoading = false;
    roles: Role[] = [];
    selectedRoleIds: number[] = [];
    selectedRoleNames: TranslatableKey[] = [''];
    notifySubject = false;

    constructor(
        protected auth: AuthService,
        private route: ActivatedRoute,
        private notify: NotifyService,
        private location: Location
    ) {
        super();
        this.changeRequestTemplate = this.route.snapshot.data?.models?.get('changeRequestTemplate');

        if (!this.changeRequestTemplate.approvalWorkflow) {
            this.changeRequestTemplate.approvalWorkflow = new ApprovalWorkflow({
                entityId: this.changeRequestTemplate.id,
                entityType: 'ChangeRequestTemplate',
            });
        }

        this.approvalWorkflow = this.changeRequestTemplate.approvalWorkflow;
    }

    async ngOnInit(): Promise<void> {
        await this.getRoles();
        this.selectedRoleIds = this.changeRequestTemplate.roles.map((role) => role.id);
        this.selectedRoleNames = this.changeRequestTemplate.roles.map(
            (role) => RoleNameDisplayOptions[role.name ?? ''] ?? role.name
        );
    }

    updateRoles($event: number[]): void {
        this.selectedRoleIds = $event;
        this.selectedRoleNames = this.selectedRoleIds.map((roleId) => {
            const role = this.roles.find((role) => role.id == roleId);
            return RoleNameDisplayOptions[role.name ?? ''] ?? role.name;
        });
    }

    handleNotifySubjectChanged(value: boolean): void {
        this.notifySubject = value;
    }

    async onSave(): Promise<void> {
        /*
         * TODO: We want to throw show an error toast if the template has no approvers
         *       Message - “You must add at least one approver in order to activate this template”
         */

        this.isLoading = true;

        try {
            this.changeRequestTemplate.roleIds = this.selectedRoleIds;
            this.changeRequestTemplate.notifySubject = this.notifySubject;

            this.approvalWorkflow.steps = this.approvalWorkflow.steps.map((step) => step.getAttributes());
            await Promise.all([this.approvalWorkflow.save(), this.changeRequestTemplate.save()]);

            this.notify.success('change-requests.requestTemplateUpdateSuccessful');
            this.isLoading = false;
            this.location.back();
        } catch (e) {
            this.notify.error(e);
        }

        this.isLoading = false;
    }

    private async getRoles(): Promise<void> {
        const withPermissions = ['manageAll', 'manageDirectAndIndirectReports', 'manageDirectReports'];

        await Role.where('company_id', this.auth.company.id)
            .with('permissions')
            .orderBy('id', 'ASC')
            .get()
            .then(([roles, _]: [Role[], unknown]) => {
                this.roles = roles.filter((role) =>
                    withPermissions.some((withPermission) =>
                        role.permissions.map((permission) => permission.name).includes(withPermission)
                    )
                );
            })
            .catch((err) => this.notify.error(err));
    }
}
